import { yupResolver } from "@hookform/resolvers/yup";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { Suspense } from "react";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { DefaultAddressValidationSchema } from "@/components/Form/RHF/FormBlocks/Address/partials/defaultAddress.schema";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { RadioFieldArray } from "@/components/Form/RHF/FormFields/RadioFieldArray/radioFieldArray.component";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { VatField } from "@/components/Form/RHF/FormFields/VatField/vatField.container";
import { RequiredVatSchema } from "@/components/Form/RHF/FormFields/VatField/vatField.schema";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { getVatPrefixes } from "@/redux/modules/api/data/vatPrefixes/vatPrefixes.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getEmptyAddress } from "@/utils/address/address";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import { Config } from "@/utils/config.utils";
import { GiftCardType } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import * as RHFGiftCardValidationRules from "@/utils/validation/schemas/giftCard.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

import { BusinessAddressFormBlock } from "../Form/RHF/FormBlocks/Address/addressFormBlock.container";
import { NumericField } from "../Form/RHF/FormFields/NumericField/NumericField.component";
import { OptionalPhoneSchema } from "../Form/RHF/FormFields/PhoneField/phoneField.schema";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import { RequestBusinessGiftCardRequest } from "./businessGiftCardRequest.types";

const FORM_ID = "compayGiftCardRequest";

const BusinessGiftCardRequestFormValidationSchema = yup
    .object({
        quantity: RHFGiftCardValidationRules.giftCardQuantity,
        value: RHFGiftCardValidationRules.giftCardAmountInEuros,
        contact: RHFGiftCardValidationRules.contact,
        email: RHFUserValidationRules.emailWithNB,
        orderNumber: RHFGeneralValidationRules.optionalOrderId,
        remarks: RHFGeneralValidationRules.optionalMessage(500),
        type: yup
            .mixed<GiftCardType>()
            .oneOf(Object.values(GiftCardType))
            .required()
    })
    .concat(OptionalPhoneSchema)
    .concat(DefaultAddressValidationSchema)
    .concat(RequiredVatSchema);

type FormValues = yup.InferType<
    typeof BusinessGiftCardRequestFormValidationSchema
>;

const TypeConfig: Record<string, Record<GiftCardType, string>> = {
    nl: {
        [GiftCardType.Gift]: "CADEAU",
        [GiftCardType.HappyHolidays]: "PRETTIGE_FEESTEN",
        [GiftCardType.Congratulations]: "GEFELICITEERD",
        [GiftCardType.Digital]: "DIGITAAL"
    },
    fr: {
        [GiftCardType.Gift]: "CADEAU",
        [GiftCardType.HappyHolidays]: "JOYEUSES_FETES",
        [GiftCardType.Congratulations]: "FELICITATIONS",
        [GiftCardType.Digital]: "DIGITALE"
    }
};

const translateType = (type: GiftCardType) => {
    return TypeConfig[Config.getLanguage()][type];
};

type BusinessGiftCardRequestProps = {
    isLoading?: boolean;
    onSubmit: (request: RequestBusinessGiftCardRequest) => Promise<any>;
};

const BusinessGiftCardRequest = ({
    isLoading = false,
    onSubmit
}: BusinessGiftCardRequestProps) => {
    const { validateBusiness, isValidating } = useAddressValidationContext();
    const { t } = useGranite();
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );
    const vatPrefixes = useSelector(getVatPrefixes, shallowEqual);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            type: GiftCardType.Digital,
            vatInfo: {
                vatNr: "",
                vatNrPrefix: Country.Be,
                companyName: ""
            },
            ...getEmptyAddress(AddressType.Delivery),
            contact: "",
            email: "",
            phone: "",
            phonePrefix: "+32",
            orderNumber: "",
            remarks: "",
            quantity: 1,
            value: 0
        },
        resolver: yupResolver(BusinessGiftCardRequestFormValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const onHandleSubmit = async (formValues: FormValues) => {
        clearMessageById(BPOSTInvalidAddressMessageId);
        clearMessageById("business-giftcard-request-success");
        clearMessageById("business-giftcard-request-failure");

        const validationResult = await validateBusiness({
            companyName: formValues.vatInfo.companyName,
            street: formValues.street,
            number: formValues.number,
            zipCode: formValues.zipCode,
            city: formValues.city,
            country: formValues.country,
            box: formValues.box || "",
            addressing: null,
            type: null,
            isBpostField: !!formValues.selected,
            currentStatus: AddressValidationStatus.Unknown
        });

        if (validationResult === AddressValidationStatus.Invalid) {
            broadcastMessage({
                id: BPOSTInvalidAddressMessageId,
                text: t("bpost.validation.address.invalid"),
                status: "warning",
                dismissible: true
            });
            return;
        }

        if (validationResult === AddressValidationStatus.Closed) {
            return;
        }

        const values: RequestBusinessGiftCardRequest = {
            contact: formValues.contact,
            email: formValues.email,
            contactPhone: formValues.phone
                ? `${formValues.phonePrefix}${formValues.phone}`
                : undefined,
            orderNumber: formValues.orderNumber || undefined,
            remarks: formValues.remarks || undefined,
            quantity: formValues.quantity,
            value: formValues.value,
            type: translateType(formValues.type),
            companyName: formValues.vatInfo.companyName,
            vatNumber: `${formValues.vatInfo.vatNrPrefix}${formValues.vatInfo.vatNr}`,
            street: formValues.street,
            number: formValues.number,
            box: formValues.box ? formValues.box : undefined,
            zipCode: formValues.zipCode,
            city: formValues.city,
            country: formValues.country
        };

        if (onSubmit) {
            onSubmit(values).then(() => {
                methods.reset();
            });
        }
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <Messages messageid={BPOSTInvalidAddressMessageId} />
            <Messages messageid={"business-giftcard-request-success"} />
            <Messages messageid={"business-giftcard-request-failure"} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onHandleSubmit)}
                        >
                            <Container className={cn("px-0")}>
                                <Row>
                                    <Col>
                                        <NumericField<FormValues>
                                            name={"quantity"}
                                            autoComplete={"off"}
                                            label={t(
                                                "general.form.giftcard.quantity.label"
                                            )}
                                            placeholder={t(
                                                "general.form.giftcard.quantity.placeholder"
                                            )}
                                            required
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <NumericField<FormValues>
                                            name={"value"}
                                            label={t(
                                                "general.form.giftcard.value.label"
                                            )}
                                            placeholder={t(
                                                "general.form.giftcard.value.placeholder"
                                            )}
                                            autoComplete={"off"}
                                            required
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <RadioFieldArray<
                                            FormValues,
                                            GiftCardType
                                        >
                                            name={"type"}
                                            label={t(
                                                "general.form.giftcard.type.label"
                                            )}
                                            required={true}
                                            disabled={!initialized}
                                            options={[
                                                {
                                                    label: t(
                                                        "general.form.giftcard.label.digital"
                                                    ),
                                                    value: GiftCardType.Digital
                                                },
                                                {
                                                    label: t(
                                                        "general.form.giftcard.label.present"
                                                    ),
                                                    value: GiftCardType.Gift
                                                }
                                            ]}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col
                                        xs={12}
                                        md={5}
                                    >
                                        <VatField<FormValues>
                                            label={t(
                                                "general.form.vatNr.label"
                                            )}
                                            required={true}
                                            disabled={!initialized}
                                            vatPrefixes={vatPrefixes}
                                        />
                                    </Col>
                                    <Col
                                        xs={12}
                                        md={7}
                                    >
                                        <TextField<FormValues>
                                            name="vatInfo.companyName"
                                            label={t(
                                                "general.form.companyName.label"
                                            )}
                                            required
                                            autoComplete={"organization"}
                                            placeholder={t(
                                                "general.form.companyName.placeholder"
                                            )}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <BusinessAddressFormBlock<
                                            FormValues,
                                            AddressType.Default
                                        >
                                            addressType={AddressType.Default}
                                            skipValidation={true}
                                            addressStatus={
                                                AddressValidationStatus.Unknown
                                            }
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <TextField<FormValues>
                                            name="contact"
                                            label={t(
                                                "general.form.giftcard.contact.label"
                                            )}
                                            required
                                            placeholder={t(
                                                "general.form.giftcard.contact.placeholder"
                                            )}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <TextField<FormValues>
                                            name="email"
                                            label={t(
                                                "general.form.email.label"
                                            )}
                                            required
                                            autoComplete={"email"}
                                            placeholder={t(
                                                "general.form.email.placeholder"
                                            )}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <PhoneField<FormValues>
                                            autoComplete={"tel-national"}
                                            label={t(
                                                "general.form.giftcard.phoneNumber.label"
                                            )}
                                            placeholder={t(
                                                "general.form.giftcard.phoneNumber.placeholder"
                                            )}
                                            required={false}
                                            disabled={!initialized}
                                            phonenumberPrefixes={
                                                phonenumberPrefixes
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <TextField<FormValues>
                                            name="orderNumber"
                                            label={t(
                                                "general.form.giftcard.orderNumber.label"
                                            )}
                                            placeholder={t(
                                                "general.form.giftcard.orderNumber.placeholder"
                                            )}
                                            required={false}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <TextAreaField<FormValues>
                                            name="remarks"
                                            label={t(
                                                "general.form.giftcard.remarks.label"
                                            )}
                                            placeholder={t(
                                                "general.form.giftcard.remarks.placeholder"
                                            )}
                                            required={false}
                                            maxLength={500}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Button
                                            test="businessGiftCardRequest-button-submit"
                                            type={"submit"}
                                            loading={isLoading || isValidating}
                                            disabled={!initialized}
                                        >
                                            {t("general.form.send")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Container>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </Suspense>
    );
};

export default BusinessGiftCardRequest;
